import React, { useEffect, useState } from 'react';
import { Lock, KeyRound, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/**
 * Lightweight client-side gate for the Fees section.
 * Prompts HT to set a memorable 4-6 digit PIN on first visit, then asks for it
 * on every subsequent entry to the Fees view. The hash is stored in localStorage
 * keyed by the HT's user id so it's per-user. The PIN is never sent to the
 * backend — this is a UX-level "accident prevention" gate, not a security
 * boundary (the auth token is what actually protects the underlying APIs).
 */
const FeesPinGate = ({ children, userId = 'self' }) => {
  const storageKey = `mcai_fees_pin::${userId}`;
  const [storedPin, setStoredPin] = useState(null);
  const [mode, setMode] = useState('locked'); // 'locked' | 'setup' | 'unlocked'
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [showPin, setShowPin] = useState(false);

  useEffect(() => {
    const existing = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
    setStoredPin(existing);
    setMode(existing ? 'locked' : 'setup');
  }, [storageKey]);

  const handleSetup = (e) => {
    e?.preventDefault();
    if (!/^\d{4,6}$/.test(pin)) {
      toast.error('PIN must be 4 to 6 digits');
      return;
    }
    if (pin !== pinConfirm) {
      toast.error('PINs do not match');
      return;
    }
    localStorage.setItem(storageKey, pin);
    setStoredPin(pin);
    setMode('unlocked');
    toast.success('Fees PIN set — you will be prompted on every visit');
    setPin('');
    setPinConfirm('');
  };

  const handleUnlock = (e) => {
    e?.preventDefault();
    if (pin === storedPin) {
      setMode('unlocked');
      setPin('');
    } else {
      toast.error('Incorrect PIN');
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset your Fees PIN? You will need to set a new one on the next visit.')) {
      localStorage.removeItem(storageKey);
      setStoredPin(null);
      setMode('setup');
    }
  };

  if (mode === 'unlocked') {
    return (
      <div>
        <div className="mb-4 flex items-center justify-end gap-2 text-xs text-gray-500">
          <Lock size={12} />
          <span>Fees session unlocked</span>
          <button
            onClick={handleReset}
            className="text-purple-600 hover:text-purple-800 underline ml-2"
            data-testid="fees-pin-reset"
          >
            Reset PIN
          </button>
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-12 bg-white rounded-2xl shadow-sm border border-gray-100 p-8" data-testid="fees-pin-gate">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 text-white mb-4 shadow-md">
          {mode === 'setup' ? <KeyRound size={28} /> : <Lock size={28} />}
        </div>
        <h2 className="text-xl font-bold text-gray-800">
          {mode === 'setup' ? 'Create a Fees PIN' : 'Enter Fees PIN'}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {mode === 'setup'
            ? 'Pick a 4–6 digit PIN you will remember. You will be asked for it every time you open the Fees section.'
            : 'Enter the PIN you set up to access Fees.'}
        </p>
      </div>

      <form onSubmit={mode === 'setup' ? handleSetup : handleUnlock} className="mt-6 space-y-3">
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">PIN</label>
          <div className="relative">
            <input
              type={showPin ? 'text' : 'password'}
              inputMode="numeric"
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 text-lg tracking-widest text-center focus:outline-none focus:border-purple-500"
              placeholder="••••"
              data-testid="fees-pin-input"
            />
            <button
              type="button"
              onClick={() => setShowPin(s => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              tabIndex={-1}
              aria-label="Toggle PIN visibility"
            >
              {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {mode === 'setup' && (
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Confirm PIN</label>
            <input
              type={showPin ? 'text' : 'password'}
              inputMode="numeric"
              value={pinConfirm}
              onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full border-2 border-gray-200 rounded-lg px-3 py-2.5 text-lg tracking-widest text-center focus:outline-none focus:border-purple-500"
              placeholder="••••"
              data-testid="fees-pin-confirm"
            />
          </div>
        )}

        <Button
          type="submit"
          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white"
          data-testid="fees-pin-submit"
        >
          {mode === 'setup' ? 'Set PIN & Continue' : 'Unlock'}
        </Button>
      </form>

      {mode === 'locked' && (
        <p className="text-xs text-center text-gray-400 mt-4">
          Forgot the PIN?{' '}
          <button onClick={handleReset} className="text-purple-600 underline">
            Reset
          </button>
        </p>
      )}
    </div>
  );
};

export default FeesPinGate;
